import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Typography,
  Box,
  Card,
  TextField,
  Container,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Popover,
  Switch,
  Tooltip,
  useMediaQuery,
  IconButton,
  Checkbox,
  Radio,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Snackbar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CalendarMonth,
  PlayCircleOutline as PlayCircleOutlineIcon,
} from '@mui/icons-material';
import { Calendar } from 'react-date-range';
import { format, addDays, parseISO } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import PageHeader from './PageHeader';
import RecordingDayTimeline from './RecordingDayTimeline';
import { RecordingPlaybackModal } from './RecordingPlaybackModal';
import { authenticatedFetch, getCameraStreams, getRtspSnapshot, isRecordingWebPlayable } from '../utils/api';
import { buildApiUrl } from '../config/api';
import CloseIcon from '@mui/icons-material/Close';

const TIMELINE_DAYS_PER_PAGE = 7;

/** Local yyyy-MM-dd for a recording instant; matches RecordingDayTimeline (SQL `… …` → `T` before parsing). */
const recordingLocalDateKey = (iso) => {
  if (iso == null) return null;
  const t = String(iso).trim();
  if (!t) return null;
  const normalized = /^\d{4}-\d{2}-\d{2} \d{1,2}:\d{2}/.test(t) ? t.replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T') : t;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return null;
  return format(d, 'yyyy-MM-dd');
};

/** HH:mm (24h, from API / time input) -> e.g. "9:00 AM" for read-only schedule cards only. */
const formatScheduleTimeDisplay12h = (hhmm) => {
  if (hhmm == null || String(hhmm).trim() === '') return '—';
  const m = String(hhmm).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return String(hhmm);
  const h = parseInt(m[1], 10);
  const min = m[2];
  if (Number.isNaN(h) || h < 0 || h > 23) return String(hhmm);
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${min}\u00A0${ampm}`;
};

/** Start `HH:mm` + duration (hours, may be fractional) → end `HH:mm` (24h), wrapped within a day. */
const computeScheduleEndTimeHhmm = (startHhmm, durationHours) => {
  const m = String(startHhmm || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(min) || h < 0 || h > 23 || min < 0 || min > 59) return null;
  const dur = parseFloat(durationHours);
  if (!Number.isFinite(dur) || dur < 0) return null;
  let totalMin = Math.round(h * 60 + min + dur * 60);
  totalMin = ((totalMin % (24 * 60)) + (24 * 60)) % (24 * 60);
  const eh = Math.floor(totalMin / 60);
  const em = totalMin % 60;
  return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
};

const formatScheduleEndDisplay12h = (startHhmm, durationHours) => {
  const end = computeScheduleEndTimeHhmm(startHhmm, durationHours);
  return end ? formatScheduleTimeDisplay12h(end) : '—';
};

/** Muted light red — shared by recordings table delete icon and delete-confirm modal button. */
const RECORDINGS_DELETE_RED = 'hsl(0, 56.70%, 49.80%)';
const RECORDINGS_DELETE_RED_HOVER_BG = 'hsla(0, 50%, 60%, 0.16)';
const RECORDINGS_DELETE_RED_BUTTON_HOVER = 'hsl(0, 50%, 52%)';

/** Date range accent — matches audit logs filters. */
const RECORDINGS_FILTER_PRIMARY = '#40518A';

const chipHueFromId = (id) => {
  let h = 0;
  const s = String(id || '');
  for (let i = 0; i < s.length; i += 1) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return ((h % 360) + 360) % 360;
};

/** Align with backend UUID rules (hyphenated or 32 hex chars). */
const normalizeCameraConfigId = (raw) => {
  const s = raw == null ? '' : String(raw).trim();
  if (!s) return null;
  if (
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(s)
  ) {
    return s.toLowerCase();
  }
  if (/^[0-9a-fA-F]{32}$/.test(s)) {
    const x = s.toLowerCase();
    return `${x.slice(0, 8)}-${x.slice(8, 12)}-${x.slice(12, 16)}-${x.slice(16, 20)}-${x.slice(20, 32)}`;
  }
  return null;
};

/**
 * Canonical id for schedules: prefer lowercase UUID formatting; fallback to trimmed DB id (≤36 chars)
 * so every camera listed by /camera-streams appears here (Lobby was skipped when strict UUID parse failed).
 */
const coerceRecordingCameraId = (raw) => {
  const n = normalizeCameraConfigId(raw);
  if (n) return n;
  const t = raw == null ? '' : String(raw).trim();
  if (!t || t.length > 36) return null;
  return t;
};

const dedupeNormalizedCameraIds = (ids) => {
  const seen = new Set();
  const out = [];
  for (let i = 0; i < (ids?.length ?? 0); i += 1) {
    const c = coerceRecordingCameraId(ids[i]);
    if (!c) continue;
    const key = c.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(c);
    }
  }
  return out;
};

/** Same substream test the recorder uses when a slot has no saved stream. */
const isSubStreamUrl = (url) => {
  const u = String(url || '');
  return u.toLowerCase().includes('subtype=1') || u.endsWith('/102');
};

const streamRoleLabel = (url, index) => {
  const u = String(url || '').trim();
  const low = u.toLowerCase();
  if (isSubStreamUrl(u)) return 'Sub';
  if (/subtype=0(?:\D|$)/.test(low) || /(?:^|\/)101$/.test(u)) return 'Main';
  if (/subtype=2(?:\D|$)/.test(low) || /(?:^|\/)103$/.test(u)) return 'Third';
  return `Stream ${index + 1}`;
};

/** Active camera streams in configured order. A camera with only rtsp_url yields one row. */
const streamsForCamera = (camera) => {
  const rows = Array.isArray(camera?.streams) ? camera.streams : [];
  const active = rows
    .filter((stream) => {
      if (!stream || !String(stream.stream_url || '').trim()) return false;
      return stream.is_active !== false && stream.is_active !== 0;
    })
    .map((stream) => ({
      id: String(stream.id || '').trim(),
      url: String(stream.stream_url).trim(),
    }));
  if (rows.length) return active;
  const primary = String(camera?.rtsp_url || '').trim();
  if (primary) return [{ id: '', url: primary }];
  return [];
};

const defaultStreamForCamera = (streams) => {
  if (!Array.isArray(streams) || !streams.length) return null;
  const sub = streams.find((stream) => isSubStreamUrl(stream.url));
  if (sub) return sub;
  return streams.find((stream) => !isSubStreamUrl(stream.url)) || streams[0];
};

const streamIdsEqual = (left, right) => String(left || '').trim().toLowerCase() === String(right || '').trim().toLowerCase();

const streamRoleForCamera = (camera, streamId) => {
  const streams = streamsForCamera(camera);
  if (!streams.length) return '';
  const saved = streamId == null
    ? null
    : streams.find((stream) => streamIdsEqual(stream.id, streamId));
  const chosen = saved || defaultStreamForCamera(streams);
  if (!chosen) return '';
  const index = streams.findIndex((stream) => stream.id === chosen.id && stream.url === chosen.url);
  return streamRoleLabel(chosen.url, index < 0 ? 0 : index);
};

const normalizeCameraStreams = (raw) => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const item of raw) {
    const cid = coerceRecordingCameraId(item?.camera_config_id ?? item?.cameraConfigId);
    if (!cid) continue;
    const key = cid.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const sid = item?.stream_id ?? item?.streamId;
    out.push({
      cameraConfigId: cid,
      streamId: sid == null ? '' : String(sid).trim(),
    });
  }
  return out;
};

const RecordingInterface = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  /** @type {[{ startTime: string, durationHours: string, enabled: boolean, cameraConfigIds: string[], cameraStreams: { cameraConfigId: string, streamId: string }[] }]} */
  const [scheduleWindows, setScheduleWindows] = useState([]);
  /** Synchronous copy so a background save always sends the list the user already sees. */
  const scheduleWindowsRef = useRef([]);
  /** Latest queued schedule write. Older responses must not overwrite a newer edit. */
  const scheduleSaveGenerationRef = useRef(0);
  const scheduleSaveChainRef = useRef(Promise.resolve());
  const [configuredCameras, setConfiguredCameras] = useState([]);
  /** Camera id → stream id for the slot being added or edited. Presence means the camera is selected. */
  const [slotStreamByCamera, setSlotStreamByCamera] = useState({});
  const [streamPreview, setStreamPreview] = useState({
    open: false,
    loading: false,
    error: '',
    image: '',
    label: '',
  });
  const [editingSlotIndex, setEditingSlotIndex] = useState(null);
  const [togglingSlotIndex, setTogglingSlotIndex] = useState(null);
  const [scheduleDeleteConfirmIndex, setScheduleDeleteConfirmIndex] = useState(null);
  const [addSlotModalOpen, setAddSlotModalOpen] = useState(false);
  const [addSlotStartTime, setAddSlotStartTime] = useState('09:00');
  const [addSlotDurationHours, setAddSlotDurationHours] = useState('2');
  const [addSlotSaving, setAddSlotSaving] = useState(false);
  const [slotModalSnackbar, setSlotModalSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error',
  });
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [scheduleError, setScheduleError] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [recordingsLoading, setRecordingsLoading] = useState(true);
  const [recordingTimelineDates, setRecordingTimelineDates] = useState([]);
  const [recordingsPagination, setRecordingsPagination] = useState({
    limit: TIMELINE_DAYS_PER_PAGE,
    offset: 0,
    current_page: 1,
    total: 0,
    total_pages: 0,
  });
  const [recordingsError, setRecordingsError] = useState(null);
  /** Drops stale timeline responses so a background delete is not painted back onto the page. */
  const timelineLoadGenRef = useRef(0);
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  /** Bumps on each open so we can seek again when the same recording is chosen from the timeline. */
  const [playbackSessionId, setPlaybackSessionId] = useState(0);
  const [playInitialSeekSeconds, setPlayInitialSeekSeconds] = useState(null);
  const [deleteDayConfirmOpen, setDeleteDayConfirmOpen] = useState(false);
  const [dayPendingDelete, setDayPendingDelete] = useState(null);
  const [recordingsFilterByDate, setRecordingsFilterByDate] = useState(false);
  const [recordingsDateRange, setRecordingsDateRange] = useState(() => [
    { startDate: new Date(), endDate: new Date(), key: 'selection' },
  ]);
  /** Popover-only selection; committed to `recordingsDateRange` on Apply so calendar clicks do not refetch. */
  const [recordingsDatePickerDraft, setRecordingsDatePickerDraft] = useState(() => [
    { startDate: new Date(), endDate: new Date(), key: 'selection' },
  ]);
  const [recordingsDatePickerAnchor, setRecordingsDatePickerAnchor] = useState(null);
  const recordingsDatePickerOpen = Boolean(recordingsDatePickerAnchor);

  const applyScheduleResponse = useCallback((data) => {
    const fmtHm = (hour, minute) =>
      `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    // Always trust `windows` when API sends an array (including [] after last delete).
    // Do not fall back to legacy two-slot defaults when windows is empty — that was re-adding Slot 2.
    if (Array.isArray(data.windows)) {
      const next = data.windows.map((w) => {
        const cameraStreams = normalizeCameraStreams(w.camera_streams);
        return {
          startTime: fmtHm(w.hour, w.minute),
          durationHours: String(w.duration_hours ?? 2),
          enabled: w.enabled !== false,
          cameraStreams,
          cameraConfigIds: cameraStreams.length
            ? cameraStreams.map((row) => row.cameraConfigId)
            : dedupeNormalizedCameraIds(
              Array.isArray(w.camera_config_ids) ? w.camera_config_ids : [],
            ),
        };
      });
      scheduleWindowsRef.current = next;
      setScheduleWindows(next);
      return;
    }
    const legacy = [
      {
        startTime: data.window1_start_time ?? data.start_time ?? '09:00',
        durationHours: String(data.window1_duration_hours ?? data.duration_hours ?? 2),
        enabled: true,
        cameraConfigIds: [],
        cameraStreams: [],
      },
      {
        startTime: data.window2_start_time ?? '14:00',
        durationHours: String(data.window2_duration_hours ?? 2),
        enabled: true,
        cameraConfigIds: [],
        cameraStreams: [],
      },
    ];
    scheduleWindowsRef.current = legacy;
    setScheduleWindows(legacy);
  }, []);

  const buildScheduleWindowsPayload = useCallback(() => {
    const parseHm = (hhmm) => {
      const m = String(hhmm || '09:00').trim().match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return { hour: 9, minute: 0 };
      return { hour: parseInt(m[1], 10), minute: parseInt(m[2], 10) };
    };
    return scheduleWindowsRef.current.map((sw) => {
      const h = parseHm(sw.startTime);
      const cameraStreams = normalizeCameraStreams(sw.cameraStreams);
      const cameraConfigIds = cameraStreams.length
        ? cameraStreams.map((row) => row.cameraConfigId)
        : dedupeNormalizedCameraIds(Array.isArray(sw.cameraConfigIds) ? sw.cameraConfigIds : []);
      return {
        ...h,
        duration_hours: parseFloat(sw.durationHours) || 2,
        enabled: sw.enabled !== false,
        camera_config_ids: cameraConfigIds,
        camera_streams: cameraStreams.map((row) => ({
          camera_config_id: row.cameraConfigId,
          stream_id: row.streamId || null,
        })),
      };
    });
  }, []);

  const loadSchedule = useCallback(async () => {
    setScheduleError(null);
    setScheduleLoading(true);
    try {
      const res = await authenticatedFetch('/recording/schedule');
      if (!res.ok) throw new Error(res.status === 401 ? 'Unauthorized' : 'Failed to load schedule');
      const data = await res.json();
      applyScheduleResponse(data);
    } catch (e) {
      setScheduleError(e.message || 'Failed to load schedule');
    } finally {
      setScheduleLoading(false);
    }
  }, [applyScheduleResponse]);

  const loadRecordingTimelinePage = useCallback(async (page = 1, options = {}) => {
    const { triggerScan = false, filters: filtersOpt, silent = false } = options;
    const f = filtersOpt ?? {
      dateFrom: recordingsFilterByDate ? format(recordingsDateRange[0].startDate, 'yyyy-MM-dd') : null,
      dateTo: recordingsFilterByDate ? format(recordingsDateRange[0].endDate, 'yyyy-MM-dd') : null,
    };
    const gen = ++timelineLoadGenRef.current;
    if (!silent) {
      setRecordingsError(null);
      setRecordingsLoading(true);
    }
    try {
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(TIMELINE_DAYS_PER_PAGE),
      });
      if (triggerScan) {
        params.append('scan', 'true');
      }
      if (f.dateFrom) params.set('date_from', f.dateFrom);
      if (f.dateTo) params.set('date_to', f.dateTo);
      const res = await authenticatedFetch(`/recording/recordings/timeline-page?${params.toString()}`);
      if (!res.ok) throw new Error(res.status === 401 ? 'Unauthorized' : 'Failed to load recordings');
      const data = await res.json();
      if (timelineLoadGenRef.current !== gen) return;
      setRecordings(data.recordings || []);
      setRecordingTimelineDates(Array.isArray(data.dates) ? data.dates : []);
      const pag = data.pagination;
      setRecordingsPagination({
        limit: pag?.limit ?? TIMELINE_DAYS_PER_PAGE,
        offset: pag?.offset ?? 0,
        total: pag?.total ?? 0,
        total_pages: pag?.total_pages ?? 1,
        current_page: pag?.current_page ?? page,
      });
    } catch (e) {
      if (timelineLoadGenRef.current !== gen) return;
      if (!silent) {
        setRecordingsError(e.message || 'Failed to load recordings');
        setRecordings([]);
        setRecordingTimelineDates([]);
        setRecordingsPagination((prev) => ({ ...prev, total: 0, total_pages: 0 }));
      }
    } finally {
      if (timelineLoadGenRef.current === gen) {
        setRecordingsLoading(false);
      }
    }
  }, [recordingsDateRange, recordingsFilterByDate]);

  const handleRecordingsPageChange = useCallback((_e, page) => {
    loadRecordingTimelinePage(page);
  }, [loadRecordingTimelinePage]);

  useEffect(() => {
    loadSchedule();
    loadRecordingTimelinePage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const payload = await getCameraStreams();
        if (!cancelled) setConfiguredCameras(payload.items || []);
      } catch {
        if (!cancelled) setConfiguredCameras([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cameraLabelsById = useMemo(() => {
    const m = {};
    for (const c of configuredCameras) {
      const cid = coerceRecordingCameraId(c?.id);
      if (!cid) continue;
      const lb = (c.label || c.name || '').trim();
      m[cid] = lb || `Camera ${String(cid).slice(0, 8)}…`;
      m[cid.toLowerCase()] = m[cid];
    }
    return m;
  }, [configuredCameras]);

  /** Deduped cameras for multi-select — stable IDs for Autocomplete equality. */
  const cameraSelectOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of configuredCameras) {
      if (!c) continue;
      const id = coerceRecordingCameraId(c.id);
      if (!id) continue;
      const key = id.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ ...c, id });
    }
    return out;
  }, [configuredCameras]);

  /** Camera name plus the stream role this slot will record. */
  const scheduleCameraChipLabel = useCallback(
    (cidRaw, streamId) => {
      const cid = coerceRecordingCameraId(cidRaw) || String(cidRaw || '').trim();
      if (!cid) return String(cidRaw ?? '').trim() || '—';
      const opt = cameraSelectOptions.find((c) => coerceRecordingCameraId(c.id) === cid);
      const fromOpt = (opt?.label || '').trim();
      const mapped = cameraLabelsById[cid] ?? cameraLabelsById[String(cid).toLowerCase()];
      const name = fromOpt || mapped || (cid.length > 12 ? `Camera ${cid.slice(0, 8)}…` : cid);
      const role = streamRoleForCamera(opt, streamId);
      return role ? `${name} · ${role}` : name;
    },
    [cameraSelectOptions, cameraLabelsById],
  );

  const handleOpenPlay = (rec, opts = {}) => {
    if (!isRecordingWebPlayable(rec)) return;
    const sk = opts?.seekSeconds;
    setPlayInitialSeekSeconds(
      sk != null && typeof sk === 'number' && Number.isFinite(sk) && sk >= 0 ? sk : null
    );
    setPlaybackSessionId((n) => n + 1);
    setSelectedRecording(rec);
    setPlayModalOpen(true);
  };

  const handleClosePlay = useCallback(() => {
    setPlayModalOpen(false);
    setSelectedRecording(null);
    setPlayInitialSeekSeconds(null);
  }, []);

  const putScheduleWindows = async (windows) => {
    const res = await authenticatedFetch('/recording/schedule', {
      method: 'PUT',
      body: JSON.stringify({ windows }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = data?.detail;
      throw new Error(typeof detail === 'string' ? detail : 'Failed to update schedule');
    }
    return data;
  };

  /** Run schedule writes one after another so a slow delete cannot overwrite a later edit. */
  const enqueueSchedulePut = (windows) => {
    const generation = ++scheduleSaveGenerationRef.current;
    const run = scheduleSaveChainRef.current
      .catch(() => {})
      .then(async () => {
        const data = await putScheduleWindows(windows);
        return { generation, data };
      });
    scheduleSaveChainRef.current = run.then(() => {}, () => {});
    return run;
  };

  const applyScheduleIfLatest = (generation, data) => {
    if (scheduleSaveGenerationRef.current === generation) {
      applyScheduleResponse(data);
    }
  };

  const handleToggleSlot = async (index) => {
    const before = scheduleWindowsRef.current;
    if (index < 0 || index >= before.length) return;
    const nextUi = before.map((slot, i) =>
      i === index ? { ...slot, enabled: !(slot?.enabled !== false) } : slot
    );
    scheduleWindowsRef.current = nextUi;
    setScheduleWindows(nextUi);
    setTogglingSlotIndex(index);
    setScheduleError(null);
    const savePromise = enqueueSchedulePut(buildScheduleWindowsPayload());
    const generation = scheduleSaveGenerationRef.current;
    try {
      const { generation: savedGeneration, data } = await savePromise;
      applyScheduleIfLatest(savedGeneration, data);
    } catch (e) {
      if (scheduleSaveGenerationRef.current === generation) {
        scheduleWindowsRef.current = before;
        setScheduleWindows(before);
        setScheduleError(e.message || 'Failed to update slot');
      }
    } finally {
      setTogglingSlotIndex(null);
    }
  };

  const handleDeleteSlot = (index) => {
    const current = scheduleWindowsRef.current;
    if (index < 0 || index >= current.length) return;
    const nextUi = current.filter((_, i) => i !== index);
    scheduleWindowsRef.current = nextUi;
    setScheduleWindows(nextUi);
    setScheduleError(null);
    setEditingSlotIndex((prev) => {
      if (prev === null) return null;
      if (prev === index) {
        setAddSlotModalOpen(false);
        return null;
      }
      if (prev > index) return prev - 1;
      return prev;
    });

    const savePromise = enqueueSchedulePut(buildScheduleWindowsPayload());
    const generation = scheduleSaveGenerationRef.current;
    savePromise
      .then(({ generation: savedGeneration, data }) => {
        applyScheduleIfLatest(savedGeneration, data);
      })
      .catch(async (e) => {
        if (scheduleSaveGenerationRef.current !== generation) return;
        setScheduleError(e.message || 'Failed to delete slot');
        try {
          const res = await authenticatedFetch('/recording/schedule');
          if (!res.ok) throw new Error('Failed to load schedule');
          const data = await res.json();
          if (scheduleSaveGenerationRef.current === generation) {
            applyScheduleResponse(data);
          }
        } catch {
          scheduleWindowsRef.current = current;
          setScheduleWindows(current);
        }
      });
  };

  const handleOpenScheduleDeleteConfirm = useCallback((index) => {
    setScheduleDeleteConfirmIndex(index);
  }, []);

  const handleCloseScheduleDeleteConfirm = useCallback(() => {
    setScheduleDeleteConfirmIndex(null);
  }, []);

  const handleConfirmScheduleDelete = () => {
    if (scheduleDeleteConfirmIndex == null) return;
    const idx = scheduleDeleteConfirmIndex;
    setScheduleDeleteConfirmIndex(null);
    handleDeleteSlot(idx);
  };

  const selectionsForSlot = useCallback((slot) => {
    const saved = normalizeCameraStreams(slot?.cameraStreams);
    const savedById = new Map(saved.map((row) => [row.cameraConfigId.toLowerCase(), row.streamId]));
    const ids = dedupeNormalizedCameraIds(
      saved.length ? saved.map((row) => row.cameraConfigId) : (slot?.cameraConfigIds || []),
    );
    const map = {};
    ids.forEach((cid) => {
      const cam = cameraSelectOptions.find((option) => coerceRecordingCameraId(option.id) === cid);
      const streams = cam ? streamsForCamera(cam) : [];
      const savedId = savedById.has(cid.toLowerCase()) ? savedById.get(cid.toLowerCase()) : undefined;
      if (streams.length) {
        const match = savedId != null && streams.some((stream) => streamIdsEqual(stream.id, savedId))
          ? (streams.find((stream) => streamIdsEqual(stream.id, savedId))?.id || '')
          : (defaultStreamForCamera(streams)?.id || '');
        map[cid] = match;
      } else if (!cam) {
        map[cid] = savedId || '';
      }
    });
    return map;
  }, [cameraSelectOptions]);

  useEffect(() => {
    if (!addSlotModalOpen) return;
    setSlotStreamByCamera((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(prev).forEach((cameraId) => {
        const cam = cameraSelectOptions.find((option) => option.id === cameraId);
        if (!cam) return;
        const streams = streamsForCamera(cam);
        if (!streams.length) return;
        const current = prev[cameraId];
        const canonical = streams.find((stream) => streamIdsEqual(stream.id, current));
        if (canonical) {
          if (canonical.id !== current) {
            next[cameraId] = canonical.id;
            changed = true;
          }
          return;
        }
        next[cameraId] = defaultStreamForCamera(streams)?.id || '';
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [addSlotModalOpen, cameraSelectOptions]);

  const handleOpenAddSlotModal = () => {
    setEditingSlotIndex(null);
    setAddSlotStartTime('09:00');
    setAddSlotDurationHours('2');
    setSlotStreamByCamera({});
    setAddSlotModalOpen(true);
  };

  const handleOpenEditSlotModal = (index) => {
    const slot = scheduleWindows[index];
    if (!slot) return;
    setEditingSlotIndex(index);
    setAddSlotStartTime(slot.startTime || '09:00');
    setAddSlotDurationHours(String(slot.durationHours ?? '2'));
    setSlotStreamByCamera(selectionsForSlot(slot));
    setAddSlotModalOpen(true);
  };

  const toggleSlotCamera = (cameraId) => {
    setSlotStreamByCamera((prev) => {
      if (Object.prototype.hasOwnProperty.call(prev, cameraId)) {
        const next = { ...prev };
        delete next[cameraId];
        return next;
      }
      const cam = cameraSelectOptions.find((option) => option.id === cameraId);
      const streams = streamsForCamera(cam);
      if (!streams.length) return prev;
      return { ...prev, [cameraId]: defaultStreamForCamera(streams)?.id || '' };
    });
  };

  const selectSlotStream = (cameraId, streamId) => {
    setSlotStreamByCamera((prev) => ({ ...prev, [cameraId]: streamId }));
  };

  const handleSelectAllSlotCameras = () => {
    const next = {};
    cameraSelectOptions.forEach((camera) => {
      const streams = streamsForCamera(camera);
      if (!streams.length) return;
      next[camera.id] = defaultStreamForCamera(streams)?.id || '';
    });
    setSlotStreamByCamera(next);
  };

  const handleClearSlotCameras = () => {
    setSlotStreamByCamera({});
  };

  const openStreamPreview = async (camera, stream) => {
    const label = (camera?.label || '').trim() || 'Stream preview';
    setStreamPreview({ open: true, loading: true, error: '', image: '', label });
    try {
      const result = await getRtspSnapshot(camera?.id, stream?.url, { refresh: true });
      let image = result?.image || '';
      if (result?.image_url) {
        const snapshotUrl = buildApiUrl(result.image_url);
        const cacheSeparator = snapshotUrl.includes('?') ? '&' : '?';
        image = `${snapshotUrl}${cacheSeparator}_snapshot=${Date.now()}`;
      }
      if (!image) throw new Error(result?.message || 'Unable to fetch preview');
      setStreamPreview({ open: true, loading: false, error: '', image, label });
    } catch (err) {
      setStreamPreview({
        open: true,
        loading: false,
        error: err?.message || 'Failed to fetch preview',
        image: '',
        label,
      });
    }
  };

  const handleCloseAddSlotModal = () => {
    if (!addSlotSaving) {
      setAddSlotModalOpen(false);
      setEditingSlotIndex(null);
      setSlotModalSnackbar((prev) => ({ ...prev, open: false }));
    }
  };

  const showSlotModalSnackbar = useCallback((message, severity = 'error') => {
    setSlotModalSnackbar({ open: true, message, severity });
  }, []);

  const handleAddSlotDurationChange = (event) => {
    const raw = event.target.value;
    if (raw === '' || raw === '-') {
      setAddSlotDurationHours(raw);
      return;
    }
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) {
      setAddSlotDurationHours(raw);
      return;
    }
    if (n > 24) {
      setAddSlotDurationHours('24');
      showSlotModalSnackbar('Duration cannot be more than 24 hours', 'error');
      return;
    }
    setAddSlotDurationHours(raw);
  };

  const handleConfirmAddSlot = async () => {
    const parseHm = (hhmm) => {
      const m = String(hhmm || '09:00').trim().match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return { hour: 9, minute: 0 };
      return { hour: parseInt(m[1], 10), minute: parseInt(m[2], 10) };
    };
    const dur = parseFloat(addSlotDurationHours);
    if (!Number.isFinite(dur) || dur <= 0) {
      showSlotModalSnackbar('Duration must be greater than 0', 'error');
      return;
    }
    if (dur > 24) {
      showSlotModalSnackbar('Duration cannot be more than 24 hours', 'error');
      return;
    }
    const selectedIds = [];
    const seenSelected = new Set();
    cameraSelectOptions.forEach((camera) => {
      if (!Object.prototype.hasOwnProperty.call(slotStreamByCamera, camera.id)) return;
      selectedIds.push(camera.id);
      seenSelected.add(camera.id.toLowerCase());
    });
    Object.keys(slotStreamByCamera).forEach((cameraId) => {
      const cid = coerceRecordingCameraId(cameraId);
      if (!cid || seenSelected.has(cid.toLowerCase())) return;
      selectedIds.push(cid);
      seenSelected.add(cid.toLowerCase());
    });
    if (!selectedIds.length) {
      showSlotModalSnackbar('Select at least one camera', 'error');
      return;
    }
    setAddSlotSaving(true);
    setScheduleError(null);
    const before = scheduleWindowsRef.current;
    let generation = null;
    try {
      const existing = buildScheduleWindowsPayload();
      const cameraStreams = selectedIds.map((cameraId) => ({
        cameraConfigId: cameraId,
        streamId: slotStreamByCamera[cameraId] || slotStreamByCamera[cameraId.toLowerCase()] || '',
      }));
      const slotPayload = {
        ...parseHm(addSlotStartTime),
        duration_hours: dur,
        camera_config_ids: selectedIds,
        camera_streams: cameraStreams.map((row) => ({
          camera_config_id: row.cameraConfigId,
          stream_id: row.streamId || null,
        })),
      };
      let nextWindows;
      if (editingSlotIndex != null && editingSlotIndex >= 0 && editingSlotIndex < existing.length) {
        nextWindows = existing.map((w, i) =>
          i === editingSlotIndex
            ? {
                ...slotPayload,
                enabled: before[editingSlotIndex]?.enabled !== false,
              }
            : w,
        );
      } else {
        nextWindows = [
          ...existing,
          {
            ...slotPayload,
            enabled: true,
          },
        ];
      }
      const nextUi = nextWindows.map((w) => {
        const cameraStreams = normalizeCameraStreams(w.camera_streams);
        return {
          startTime: `${String(w.hour).padStart(2, '0')}:${String(w.minute).padStart(2, '0')}`,
          durationHours: String(w.duration_hours ?? 2),
          enabled: w.enabled !== false,
          cameraStreams,
          cameraConfigIds: cameraStreams.length
            ? cameraStreams.map((row) => row.cameraConfigId)
            : dedupeNormalizedCameraIds(w.camera_config_ids || []),
        };
      });
      scheduleWindowsRef.current = nextUi;
      setScheduleWindows(nextUi);
      const savePromise = enqueueSchedulePut(nextWindows);
      generation = scheduleSaveGenerationRef.current;
      const { generation: savedGeneration, data } = await savePromise;
      applyScheduleIfLatest(savedGeneration, data);
      setTogglingSlotIndex(null);
      setEditingSlotIndex(null);
      setAddSlotModalOpen(false);
    } catch (e) {
      if (generation != null && scheduleSaveGenerationRef.current === generation) {
        scheduleWindowsRef.current = before;
        setScheduleWindows(before);
      }
      showSlotModalSnackbar(
        e.message || (editingSlotIndex != null ? 'Failed to save slot' : 'Failed to add slot'),
        'error',
      );
    } finally {
      setAddSlotSaving(false);
    }
  };

  const scheduleEditButtonSx = { textTransform: 'none', fontWeight: 600 };
  /** Match /users active-user toggle: green on, grey off. */
  const slotEnabledSwitchSx = {
    '& .MuiSwitch-switchBase': {
      color: '#9e9e9e',
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#2e7d32',
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#2e7d32',
    },
    '& .MuiSwitch-track': {
      backgroundColor: '#bdbdbd',
      opacity: 0.85,
    },
  };
  const scheduleDeleteTextButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    color: RECORDINGS_DELETE_RED,
    borderColor: RECORDINGS_DELETE_RED,
    '&:hover': {
      borderColor: RECORDINGS_DELETE_RED,
      color: RECORDINGS_DELETE_RED_BUTTON_HOVER,
      backgroundColor: RECORDINGS_DELETE_RED_HOVER_BG,
    },
  };

  const handleOpenDeleteDayConfirm = useCallback((dateKey, dateLabel) => {
    setDayPendingDelete({ dateKey, dateLabel });
    setDeleteDayConfirmOpen(true);
  }, []);

  const handleCloseDeleteDayConfirm = useCallback(() => {
    setDeleteDayConfirmOpen(false);
    setDayPendingDelete(null);
  }, []);

  const handleRefreshRecordings = useCallback(() => {
    return loadRecordingTimelinePage(recordingsPagination.current_page, { triggerScan: true });
  }, [recordingsPagination.current_page, loadRecordingTimelinePage]);

  const clearRecordingsFilters = useCallback(() => {
    setRecordingsFilterByDate(false);
    const cleared = [{ startDate: new Date(), endDate: new Date(), key: 'selection' }];
    setRecordingsDateRange(cleared);
    setRecordingsDatePickerDraft(cleared);
    loadRecordingTimelinePage(1, { filters: { dateFrom: null, dateTo: null } });
  }, [loadRecordingTimelinePage]);

  const handleRecordingsDatePopoverApply = useCallback(() => {
    const sel = recordingsDatePickerDraft[0];
    setRecordingsDateRange([{ startDate: sel.startDate, endDate: sel.endDate, key: 'selection' }]);
    setRecordingsFilterByDate(true);
    setRecordingsDatePickerAnchor(null);
    loadRecordingTimelinePage(1, {
      filters: {
        dateFrom: format(sel.startDate, 'yyyy-MM-dd'),
        dateTo: format(sel.endDate, 'yyyy-MM-dd'),
      },
    });
  }, [loadRecordingTimelinePage, recordingsDatePickerDraft]);

  const handleRecordingsDatePopoverReset = useCallback(() => {
    const today = new Date();
    const row = [{ startDate: today, endDate: today, key: 'selection' }];
    setRecordingsDateRange(row);
    setRecordingsDatePickerDraft(row);
    setRecordingsFilterByDate(false);
    setRecordingsDatePickerAnchor(null);
    loadRecordingTimelinePage(1, { filters: { dateFrom: null, dateTo: null } });
  }, [loadRecordingTimelinePage]);

  const handleConfirmDeleteDay = () => {
    if (!dayPendingDelete?.dateKey) return;
    const dateKey = dayPendingDelete.dateKey;
    const snapshot = {
      recordings,
      dates: recordingTimelineDates,
      pagination: recordingsPagination,
    };
    const limit = recordingsPagination.limit || TIMELINE_DAYS_PER_PAGE;
    const nextTotal = Math.max(0, (recordingsPagination.total || 0) - 1);
    const nextTotalPages = nextTotal === 0 ? 0 : Math.ceil(nextTotal / limit);
    let pageAfterDelete = recordingsPagination.current_page || 1;
    if (nextTotalPages > 0 && pageAfterDelete > nextTotalPages) {
      pageAfterDelete = nextTotalPages;
    }

    setDeleteDayConfirmOpen(false);
    setDayPendingDelete(null);
    setRecordingsError(null);
    setRecordingsLoading(false);
    setRecordingTimelineDates((prev) => prev.filter((d) => d !== dateKey));
    setRecordings((prev) =>
      prev.filter((rec) => recordingLocalDateKey(rec.recorded_at || rec.start_time) !== dateKey)
    );
    setRecordingsPagination((prev) => {
      const pageLimit = prev.limit || TIMELINE_DAYS_PER_PAGE;
      const total = Math.max(0, (prev.total || 0) - 1);
      const total_pages = total === 0 ? 0 : Math.ceil(total / pageLimit);
      const current_page = total_pages === 0 ? 1 : Math.min(prev.current_page || 1, total_pages);
      return {
        ...prev,
        total,
        total_pages,
        current_page,
        offset: total === 0 ? 0 : (current_page - 1) * pageLimit,
      };
    });
    if (
      selectedRecording &&
      recordingLocalDateKey(selectedRecording.recorded_at || selectedRecording.start_time) === dateKey
    ) {
      handleClosePlay();
    }

    const genAtDelete = ++timelineLoadGenRef.current;
    const qs = new URLSearchParams({ date: dateKey });
    authenticatedFetch(`/recording/recordings/by-date?${qs.toString()}`, { method: 'DELETE' })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          const detail = data?.detail;
          throw new Error(typeof detail === 'string' ? detail : 'Failed to delete recordings for this day');
        }
        if (timelineLoadGenRef.current !== genAtDelete) return;
        await loadRecordingTimelinePage(pageAfterDelete, { silent: true });
      })
      .catch((e) => {
        setRecordingsError(e.message || 'Delete failed');
        if (timelineLoadGenRef.current !== genAtDelete) return;
        setRecordings(snapshot.recordings);
        setRecordingTimelineDates(snapshot.dates);
        setRecordingsPagination(snapshot.pagination);
      });
  };

  return (
    <Container maxWidth="lg" sx={{ width: '100%', px: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ width: '100%' }}>
        <PageHeader
          title="Recordings"
          subtitle="Schedule recording and view recent recordings"
        />

        {/* Recording schedule — table layout aligned with User Management */}
        {scheduleError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setScheduleError(null)}>
            {scheduleError}
          </Alert>
        )}
        <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Schedule Recording
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenAddSlotModal}
              disabled={scheduleLoading || addSlotSaving}
              sx={{
                ...scheduleEditButtonSx,
                width: { xs: '100%', sm: 'auto' },
                px: 1.5,
                minWidth: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              Add new slot
            </Button>
          </Box>

          {scheduleLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : scheduleWindows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No slots found. Add a slot to get started.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 960 }}>
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 100, whiteSpace: 'nowrap' }}>Slot</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 100 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 135, whiteSpace: 'nowrap' }}>Start time</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 115, whiteSpace: 'nowrap' }}>End time</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 110 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 260 }}>Cameras to record</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, minWidth: 180, whiteSpace: 'nowrap' }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scheduleWindows.map((slot, index) => {
                    const cameraIds = dedupeNormalizedCameraIds(slot.cameraConfigIds || []);
                    const slotStreams = normalizeCameraStreams(slot.cameraStreams);

                    return (
                      <TableRow
                        key={`schedule-slot-${index}`}
                        hover
                        sx={{ opacity: slot.enabled !== false ? 1 : 0.72 }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                          <Typography sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Slot {index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          {scheduleLoading ? (
                            <CircularProgress size={24} />
                          ) : (
                            <Tooltip title={slot.enabled !== false ? 'Disable slot' : 'Enable slot'} arrow>
                              <Box component="span" sx={{ display: 'inline-flex' }}>
                                {togglingSlotIndex === index ? (
                                  <CircularProgress size={28} sx={{ mx: 0.5 }} />
                                ) : (
                                  <Switch
                                    checked={slot.enabled !== false}
                                    onChange={() => handleToggleSlot(index)}
                                    disabled={togglingSlotIndex === index}
                                    sx={slotEnabledSwitchSx}
                                  />
                                )}
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {formatScheduleTimeDisplay12h(slot.startTime)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {formatScheduleEndDisplay12h(slot.startTime, slot.durationHours)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {slot.durationHours}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                            {cameraIds.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No camera is selected
                              </Typography>
                            ) : (
                              cameraIds.map((cidRaw) => {
                                const cid = coerceRecordingCameraId(cidRaw) || String(cidRaw || '').trim();
                                const savedStream = slotStreams.find(
                                  (row) => coerceRecordingCameraId(row.cameraConfigId) === cid,
                                );
                                return (
                                  <Chip
                                    key={cid}
                                    size="small"
                                    label={scheduleCameraChipLabel(
                                      cidRaw,
                                      savedStream ? savedStream.streamId : undefined,
                                    )}
                                    icon={
                                      <Box
                                        component="span"
                                        sx={{
                                          width: 10,
                                          height: 10,
                                          borderRadius: '50%',
                                          bgcolor: `hsl(${chipHueFromId(cid)}, 68%, 48%)`,
                                          ml: 0.35,
                                        }}
                                      />
                                    }
                                    sx={{ '& .MuiChip-icon': { ml: 0.5 }, mr: 0.25 }}
                                  />
                                );
                              })
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ minWidth: 180, whiteSpace: 'nowrap', verticalAlign: 'middle' }}
                        >
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Button
                              variant="outlined"
                              onClick={() => handleOpenEditSlotModal(index)}
                              disabled={scheduleLoading || addSlotSaving}
                              size="small"
                              sx={scheduleEditButtonSx}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => handleOpenScheduleDeleteConfirm(index)}
                              disabled={scheduleLoading}
                              size="small"
                              sx={scheduleDeleteTextButtonSx}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Card>



        <Dialog open={addSlotModalOpen} onClose={handleCloseAddSlotModal} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {editingSlotIndex != null ? `Edit Slot ${editingSlotIndex + 1}` : 'Add new slot'}
            <IconButton
              onClick={handleCloseAddSlotModal}
              size="small"
              disabled={addSlotSaving}
              sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Start time"
                  type="time"
                  value={addSlotStartTime}
                  onChange={(e) => setAddSlotStartTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Duration (hours)"
                  type="number"
                  value={addSlotDurationHours}
                  onChange={handleAddSlotDurationChange}
                  inputProps={{ min: 0.5, max: 24, step: 0.5 }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  fullWidth
                />
              </Stack>

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Available Cameras ({cameraSelectOptions.length})
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSelectAllSlotCameras}
                    disabled={addSlotSaving || !cameraSelectOptions.some((camera) => streamsForCamera(camera).length)}
                    sx={scheduleEditButtonSx}
                  >
                    Select all
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleClearSlotCameras}
                    disabled={addSlotSaving || Object.keys(slotStreamByCamera).length === 0}
                    sx={scheduleEditButtonSx}
                  >
                    Clear
                  </Button>
                </Stack>
              </Stack>

              <Box
                sx={{
                  maxHeight: 400,
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2.5,
                  bgcolor: 'background.paper',
                }}
              >
                {cameraSelectOptions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4, px: 2 }}>
                    No cameras are configured.
                  </Typography>
                ) : (
                  cameraSelectOptions.map((camera, cameraIndex) => {
                    const streams = streamsForCamera(camera);
                    const selected = Object.prototype.hasOwnProperty.call(slotStreamByCamera, camera.id);
                    const selectedStreamId = selected ? slotStreamByCamera[camera.id] : undefined;
                    return (
                      <Box
                        key={camera.id}
                        onClick={() => {
                          if (!addSlotSaving && streams.length) toggleSlotCamera(camera.id);
                        }}
                        sx={{
                          px: { xs: 1.5, sm: 2.5 },
                          py: 2,
                          cursor: streams.length && !addSlotSaving ? 'pointer' : 'default',
                          bgcolor: selected ? 'rgba(64, 81, 138, 0.05)' : 'transparent',
                          borderBottom: cameraIndex < cameraSelectOptions.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider',
                          opacity: streams.length ? 1 : 0.7,
                        }}
                      >
                        <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                          <Checkbox
                            checked={selected}
                            disabled={addSlotSaving || !streams.length}
                            onChange={() => toggleSlotCamera(camera.id)}
                            onClick={(event) => event.stopPropagation()}
                            sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 600,
                                lineHeight: 1.3,
                                mt: 1.1,
                                mb: 0.35,
                                color: selected ? 'primary.dark' : 'text.primary',
                              }}
                            >
                              {(camera.label || '').trim() || `Camera ${camera.id}`}
                            </Typography>
                            {streams.length ? (
                              <Stack spacing={0.4}>
                                {streams.map((stream, streamIndex) => (
                                  <Stack
                                    key={`${camera.id}-stream-${stream.id || streamIndex}`}
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    onClick={(event) => event.stopPropagation()}
                                    sx={{ minHeight: 34 }}
                                  >
                                    <Radio
                                      size="small"
                                      disabled={addSlotSaving}
                                      checked={selected && streamIdsEqual(selectedStreamId, stream.id)}
                                      onChange={() => selectSlotStream(camera.id, stream.id)}
                                      sx={{ p: 0.5 }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 700, minWidth: 52, color: 'text.secondary' }}
                                    >
                                      {streamRoleLabel(stream.url, streamIndex)}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontSize: { xs: '0.78rem', sm: '0.85rem' },
                                        lineHeight: 1.4,
                                        wordBreak: 'break-all',
                                        flex: 1,
                                      }}
                                    >
                                      {stream.url}
                                    </Typography>
                                    <Tooltip title="Preview stream">
                                      <IconButton
                                        size="small"
                                        disabled={addSlotSaving}
                                        onClick={() => openStreamPreview(camera, stream)}
                                        sx={{ p: 0.5 }}
                                      >
                                        <PlayCircleOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="error.main">
                                No stream URLs available for this camera
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseAddSlotModal} disabled={addSlotSaving} sx={scheduleEditButtonSx}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmAddSlot}
              disabled={addSlotSaving || Object.keys(slotStreamByCamera).length === 0}
              startIcon={addSlotSaving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {`Record ${Object.keys(slotStreamByCamera).length} Camera${Object.keys(slotStreamByCamera).length === 1 ? '' : 's'}`}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={streamPreview.open}
          onClose={() => setStreamPreview((prev) => ({ ...prev, open: false }))}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {streamPreview.label}
            <IconButton
              size="small"
              onClick={() => setStreamPreview((prev) => ({ ...prev, open: false }))}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {streamPreview.loading ? (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : streamPreview.error ? (
              <Alert severity="error">{streamPreview.error}</Alert>
            ) : streamPreview.image ? (
              <Box
                component="img"
                src={streamPreview.image}
                alt={streamPreview.label}
                sx={{ width: '100%', borderRadius: 1, display: 'block' }}
              />
            ) : null}
          </DialogContent>
        </Dialog>

        <Snackbar
          open={slotModalSnackbar.open}
          autoHideDuration={2000}
          onClose={() => setSlotModalSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <Alert
            onClose={() => setSlotModalSnackbar((prev) => ({ ...prev, open: false }))}
            severity={slotModalSnackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {slotModalSnackbar.message}
          </Alert>
        </Snackbar>

        <Dialog
          open={scheduleDeleteConfirmIndex != null}
          onClose={handleCloseScheduleDeleteConfirm}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Delete schedule slot?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
              {scheduleDeleteConfirmIndex != null
                ? `This will permanently remove Slot ${scheduleDeleteConfirmIndex + 1}.`
                : 'This will permanently remove the selected slot.'}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleCloseScheduleDeleteConfirm}
              sx={scheduleEditButtonSx}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmScheduleDelete}
              sx={{
                bgcolor: RECORDINGS_DELETE_RED,
                color: '#fff',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: RECORDINGS_DELETE_RED_BUTTON_HOVER,
                  boxShadow: 'none',
                },
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Recordings list */}
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.25, sm: 1 }, mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Recent recordings
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRefreshRecordings}
                disabled={recordingsLoading}
                startIcon={
                  recordingsLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  )
                }
                sx={{
                  textTransform: 'none',
                  width: { xs: '100%', sm: 130 },
                  minWidth: { xs: '100%', sm: 130 },
                  maxWidth: { xs: '100%', sm: 130 },
                  px: 1,
                  justifyContent: 'center',
                  '& .MuiButton-startIcon': { ml: 0, mr: 0.5 },
                }}
              >
                {recordingsLoading ? 'Loading' : 'Refresh'}
              </Button>
            </Stack>
          </Box>

          {/* Date range filter */}
          <Box sx={{ mb: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ md: 'center' }}
              flexWrap="wrap"
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: '1px solid',
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.85,
                  cursor: 'pointer',
                  minWidth: { xs: '100%', sm: 220 },
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': { borderColor: 'rgba(0, 0, 0, 0.87)' },
                  position: 'relative',
                }}
                onClick={(e) => {
                  setRecordingsDatePickerDraft(
                    recordingsDateRange.map((r) => ({
                      startDate: new Date(r.startDate),
                      endDate: new Date(r.endDate),
                      key: r.key,
                    }))
                  );
                  setRecordingsDatePickerAnchor(e.currentTarget);
                }}
              >
                <CalendarMonth fontSize="small" color="action" />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {recordingsFilterByDate
                    ? format(recordingsDateRange[0].startDate, 'MMM dd, yyyy')
                    : 'All dates'}
                </Typography>
                {recordingsFilterByDate && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearRecordingsFilters();
                    }}
                    sx={{
                      ml: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Stack>

            <Popover
              open={recordingsDatePickerOpen}
              anchorEl={recordingsDatePickerAnchor}
              onClose={() => setRecordingsDatePickerAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: {
                    maxWidth: '100vw',
                    width: { xs: 'calc(100vw - 24px)', sm: 'auto' },
                    overflow: 'auto',
                  },
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                <Box
                  sx={{
                    p: 2,
                    borderRight: { md: '1px solid' },
                    borderBottom: { xs: '1px solid', md: 'none' },
                    borderColor: 'divider',
                    minWidth: 140,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    Quick Select
                  </Typography>
                  <Stack spacing={0.5}>
                    {[
                      {
                        label: 'Today',
                        getValue: () => {
                          const d = new Date();
                          return { startDate: d, endDate: d };
                        },
                      },
                      {
                        label: 'Yesterday',
                        getValue: () => {
                          const d = addDays(new Date(), -1);
                          return { startDate: d, endDate: d };
                        },
                      },
                    ].map((preset) => (
                      <Button
                        key={preset.label}
                        size="small"
                        variant="text"
                        onClick={() => {
                          const { startDate, endDate } = preset.getValue();
                          setRecordingsDatePickerDraft([{ startDate, endDate, key: 'selection' }]);
                        }}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          px: 1.5,
                          py: 0.75,
                          color: 'text.primary',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </Stack>
                </Box>
                <Box
                  sx={{
                    '& .rdrCalendarWrapper': { fontSize: { xs: '0.8rem', sm: '0.875rem' } },
                    '& .rdrMonth': { width: { xs: '280px', sm: '300px' } },
                  }}
                >
                  <Calendar
                    date={recordingsDatePickerDraft[0].startDate}
                    onChange={(d) => {
                      setRecordingsDatePickerDraft([{ startDate: d, endDate: d, key: 'selection' }]);
                    }}
                    color={RECORDINGS_FILTER_PRIMARY}
                    maxDate={new Date()}
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0, flexWrap: 'wrap' }}>
                    <Button onClick={handleRecordingsDatePopoverReset} variant="outlined" size="small" startIcon={<RefreshIcon />} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      Reset
                    </Button>
                    <Button onClick={() => setRecordingsDatePickerAnchor(null)} variant="outlined" size="small" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      Cancel
                    </Button>
                    <Button onClick={handleRecordingsDatePopoverApply} variant="contained" size="small" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      Apply
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Popover>
          </Box>

          {recordingsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRecordingsError(null)}>
              {recordingsError}
            </Alert>
          )}
          {recordingsLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
          ) : recordingsPagination.total === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
              No recordings match the current filters.
            </Typography>
          ) : recordingTimelineDates.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
              No timelines to show on this page.
            </Typography>
          ) : (
            <Stack spacing={2.5} sx={{ mb: 1 }}>
              {recordingTimelineDates.map((dk) => {
                const dayTitle = format(parseISO(`${dk}T12:00:00`), 'EEE, MMM dd, yyyy');
                return (
                  <RecordingDayTimeline
                    key={dk}
                    dateKey={dk}
                    dateLabel={dayTitle}
                    recordingsFromPage={recordings.filter((rec) => recordingLocalDateKey(rec.recorded_at || rec.start_time) === dk)}
                    configuredCameras={cameraSelectOptions}
                    onPlayRecording={handleOpenPlay}
                    onDeleteDay={() => handleOpenDeleteDayConfirm(dk, dayTitle)}
                  />
                );
              })}
            </Stack>
          )}
          {!recordingsLoading && recordingsPagination.total > 0 && (
            <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 1, mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Days {recordingsPagination.offset + 1}–{Math.min(recordingsPagination.offset + recordingsPagination.limit, recordingsPagination.total)} of {recordingsPagination.total}
              </Typography>
              <Pagination
                count={recordingsPagination.total_pages}
                page={recordingsPagination.current_page}
                onChange={handleRecordingsPageChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>

        <RecordingPlaybackModal
          open={playModalOpen}
          recording={selectedRecording}
          onClose={handleClosePlay}
          initialSeekSeconds={playInitialSeekSeconds}
          playbackSessionId={playbackSessionId}
          configuredCameras={configuredCameras}
        />

        {/* Delete all recordings for a calendar day */}
        <Dialog
          open={deleteDayConfirmOpen}
          onClose={handleCloseDeleteDayConfirm}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogContent sx={{ p: 3, pt: 3 }}>
            {dayPendingDelete && (
              <Alert severity="warning" sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Delete every recording for this day?
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, mt: 1.5, color: 'text.primary' }}>
                  {dayPendingDelete.dateLabel}{' '}
                  {/* <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    ({dayPendingDelete.dateKey})
                  </Typography> */}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  All video recordings on this calendar day will be removed.
                </Typography>
              </Alert>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1 }}>
            <Button
              onClick={handleCloseDeleteDayConfirm}
              variant="outlined"
              fullWidth={isMobile}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmDeleteDay}
              fullWidth={isMobile}
              sx={{
                bgcolor: RECORDINGS_DELETE_RED,
                color: '#fff',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: RECORDINGS_DELETE_RED_BUTTON_HOVER,
                  boxShadow: 'none',
                },
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default RecordingInterface;